import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { CreateJustificationTemplateUseCase } from "@project/domain/usecases/budget/create-justification-template.usecase";
import { FetchAvailableColumnsByExpenseTypesUseCase } from "@project/domain/usecases/budget/fetch-available-columns-by-expense-types.usecase";
import { FetchExpenseOptionsUseCase } from "@project/domain/usecases/budget/fetch-expense-options.usecase";
import { FetchModelFieldsMetadataUseCase } from "@project/domain/usecases/budget/fetch-model-fields-metadata.usecase";
import { FetchProviderMetadataUseCase } from "@project/domain/usecases/budget/fetch-provider-metadata.usecase";
import { FetchSheetCatalogUseCase } from "@project/domain/usecases/budget/fetch-sheet-catalog.usecase";
import { ValidateTemplateSchemaUseCase } from "@project/domain/usecases/budget/validate-template-schema.usecase";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable } from "mobx";
import type {
    AvailableColumnsResponse,
    CustomSheetConfig,
    ExpenseOptions,
    ModelFieldsResponse,
    ProviderMetadata,
    SelectedSheet,
    SheetCatalogItem,
    TemplateWizardData,
} from "./wizard.types";

export type WizardStep =
    | "basics"
    | "sheets"
    | "custom-sheets"
    | "configuration"
    | "preview";

@injectable()
export class JustificationTemplateWizardPageViewModel extends BaseViewModel {
    @observable currentStep: WizardStep = "basics";
    @observable wizardData: TemplateWizardData = {
        name: "",
        description: "",
        version: 1,
        selectedSheets: [],
        customSheets: [],
    };

    // Catalog data loaded from backend
    @observable sheetCatalog: SheetCatalogItem[] = [];
    @observable providerMetadata: ProviderMetadata[] = [];
    @observable expenseOptions: ExpenseOptions | null = null;
    @observable modelFieldsMetadata: ModelFieldsResponse | null = null;
    @observable availableColumns: AvailableColumnsResponse | null = null;
    @observable availableTerritories: {
        key: string;
        value: string;
        label: string;
    }[] = [];

    @observable loadingCatalog = false;
    @observable loadingProviders = false;
    @observable loadingExpenseOptions = false;
    @observable loadingModelFields = false;
    @observable loadingAvailableColumns = false;
    @observable loadingTerritories = false;
    @observable validatingSchema = false;
    @observable creatingTemplate = false;

    @observable validationResult: {
        valid: boolean;
        errors: string[];
        warnings: string[];
    } | null = null;

    constructor(
        @inject(FetchProviderMetadataUseCase)
        private readonly fetchProviderMetadataUseCase: FetchProviderMetadataUseCase,
        @inject(FetchSheetCatalogUseCase)
        private readonly fetchSheetCatalogUseCase: FetchSheetCatalogUseCase,
        @inject(FetchExpenseOptionsUseCase)
        private readonly fetchExpenseOptionsUseCase: FetchExpenseOptionsUseCase,
        @inject(FetchAvailableColumnsByExpenseTypesUseCase)
        private readonly fetchAvailableColumnsByExpenseTypesUseCase: FetchAvailableColumnsByExpenseTypesUseCase,
        @inject(FetchModelFieldsMetadataUseCase)
        private readonly fetchModelFieldsMetadataUseCase: FetchModelFieldsMetadataUseCase,
        @inject(ValidateTemplateSchemaUseCase)
        private readonly validateTemplateSchemaUseCase: ValidateTemplateSchemaUseCase,
        @inject(CreateJustificationTemplateUseCase)
        private readonly createJustificationTemplateUseCase: CreateJustificationTemplateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    async initialize(): Promise<void> {
        await Promise.all([
            this.loadSheetCatalog(),
            this.loadProviderMetadata(),
            this.loadExpenseOptions(),
            this.loadModelFieldsMetadata(),
        ]);
        // Load territories lazily when needed (not blocking initialization)
        this.loadAvailableTerritories().catch((error) => {
            console.error("Error loading territories (non-critical):", error);
        });
    }

    @action
    async loadSheetCatalog(): Promise<void> {
        this.loadingCatalog = true;
        try {
            const data = await this.fetchSheetCatalogUseCase.execute();
            this.sheetCatalog = data?.sheets || [];
        } catch (error) {
            console.error("Error loading sheet catalog:", error);
            ToastManagerStore.error("Error al cargar el catálogo de hojas");
        } finally {
            this.loadingCatalog = false;
        }
    }

    @action
    async loadProviderMetadata(): Promise<void> {
        this.loadingProviders = true;
        try {
            const data = await this.fetchProviderMetadataUseCase.execute();
            this.providerMetadata = data?.providers || [];
        } catch (error) {
            console.error("Error loading provider metadata:", error);
            ToastManagerStore.error("Error al cargar los proveedores de datos");
        } finally {
            this.loadingProviders = false;
        }
    }

    @action
    async loadExpenseOptions(): Promise<void> {
        this.loadingExpenseOptions = true;
        try {
            const data = await this.fetchExpenseOptionsUseCase.execute();
            this.expenseOptions = data;
        } catch (error) {
            console.error("Error loading expense options:", error);
            ToastManagerStore.error("Error al cargar las opciones de gastos");
        } finally {
            this.loadingExpenseOptions = false;
        }
    }

    @action
    async loadModelFieldsMetadata(): Promise<void> {
        this.loadingModelFields = true;
        try {
            const data = await this.fetchModelFieldsMetadataUseCase.execute();
            this.modelFieldsMetadata = data;
        } catch (error) {
            console.error("Error loading model fields metadata:", error);
            ToastManagerStore.error(
                "Error al cargar los metadatos de los modelos",
            );
        } finally {
            this.loadingModelFields = false;
        }
    }

    @action
    async loadAvailableColumns(
        expenseTypes: string[],
        expenseSubtypes: string[],
    ): Promise<void> {
        if (expenseTypes.length === 0) {
            this.availableColumns = null;
            return;
        }

        this.loadingAvailableColumns = true;
        try {
            const data =
                await this.fetchAvailableColumnsByExpenseTypesUseCase.execute(
                    expenseTypes,
                    expenseSubtypes,
                );
            this.availableColumns = data;
        } catch (error) {
            console.error("Error loading available columns:", error);
            ToastManagerStore.error("Error al cargar las columnas disponibles");
            this.availableColumns = null;
        } finally {
            this.loadingAvailableColumns = false;
        }
    }

    @action
    async loadAvailableTerritories(): Promise<void> {
        this.loadingTerritories = true;
        try {
            // Construct the full API URL using environment variables
            const apiUrl =
                process.env.INC_API_SERVER_URL + process.env.INC_API_BASE_URL;

            // Call the API endpoint to fetch CCAA (territories)
            const response = await fetch(
                `${apiUrl}/entities_regcon_registers/cc_aa/`,
                {
                    credentials: "include", // Include cookies for authentication
                    headers: {
                        "Content-Type": "application/json",
                    },
                },
            );
            if (!response.ok) {
                throw new Error(
                    `Failed to fetch territories: ${response.status} ${response.statusText}`,
                );
            }
            const data = await response.json();
            // Transform the response to the format needed for Select options
            // API returns {key, value} where key is the territory code (e.g., "pais_vasco")
            // and value is the display name (e.g., "País Vasco")
            this.availableTerritories = data.map((item: any) => ({
                key: item.key,
                value: item.value,
                label: item.value, // Use value as label for display
            }));
        } catch (error) {
            console.error("Error loading available territories:", error);
            // Don't show error message as this is non-critical
            this.availableTerritories = [];
        } finally {
            this.loadingTerritories = false;
        }
    }

    @action
    setCurrentStep(step: WizardStep): void {
        this.currentStep = step;
    }

    @action
    updateBasicInfo(data: Partial<TemplateWizardData>): void {
        this.wizardData = {
            ...this.wizardData,
            ...data,
        };
    }

    @action
    addSheet(sheet: SheetCatalogItem): void {
        const selectedSheet: SelectedSheet = {
            ...sheet,
            customName: sheet.sheetName,
            id: `${sheet.templateKey}_${sheet.sheetName}_${Date.now()}`,
        };
        this.wizardData.selectedSheets.push(selectedSheet);
    }

    @action
    removeSheet(sheetId: string): void {
        this.wizardData.selectedSheets = this.wizardData.selectedSheets.filter(
            (s) => s.id !== sheetId,
        );
    }

    @action
    updateSheet(sheetId: string, updates: Partial<SelectedSheet>): void {
        const index = this.wizardData.selectedSheets.findIndex(
            (s) => s.id === sheetId,
        );
        if (index >= 0) {
            this.wizardData.selectedSheets[index] = {
                ...this.wizardData.selectedSheets[index],
                ...updates,
            };
        }
    }

    @action
    reorderSheets(startIndex: number, endIndex: number): void {
        const result = Array.from(this.wizardData.selectedSheets);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        this.wizardData.selectedSheets = result;
    }

    // Custom Sheets Management
    @action
    addCustomSheet(customSheet: CustomSheetConfig): void {
        this.wizardData.customSheets.push(customSheet);
    }

    @action
    removeCustomSheet(sheetId: string): void {
        this.wizardData.customSheets = this.wizardData.customSheets.filter(
            (s) => s.id !== sheetId,
        );
    }

    @action
    updateCustomSheet(
        sheetId: string,
        updates: Partial<CustomSheetConfig>,
    ): void {
        const index = this.wizardData.customSheets.findIndex(
            (s) => s.id === sheetId,
        );
        if (index >= 0) {
            this.wizardData.customSheets[index] = {
                ...this.wizardData.customSheets[index],
                ...updates,
            };
        }
    }

    @action
    async validateTemplate(): Promise<boolean> {
        this.validatingSchema = true;
        try {
            const jsonSchema = this.buildJsonSchema();
            const result =
                await this.validateTemplateSchemaUseCase.execute(jsonSchema);
            this.validationResult = result;

            if (!result.valid) {
                ToastManagerStore.error(
                    "La plantilla tiene errores de validación",
                );
            }

            return result.valid;
        } catch (error) {
            console.error("Error validating template:", error);
            ToastManagerStore.error("Error al validar la plantilla");
            return false;
        } finally {
            this.validatingSchema = false;
        }
    }

    @action
    async createTemplate(): Promise<number | null> {
        // Validate that at least one sheet exists (catalog or custom)
        if (
            this.wizardData.selectedSheets.length === 0 &&
            this.wizardData.customSheets.length === 0
        ) {
            ToastManagerStore.error(
                "Debes añadir al menos una hoja (del catálogo o personalizada)",
            );
            return null;
        }

        this.creatingTemplate = true;
        try {
            const jsonSchema = this.buildJsonSchema();

            // Generate unique template_key
            const templateKey = `CUSTOM_${this.wizardData.name
                .toUpperCase()
                .replace(/\s+/g, "_")
                .replace(/[^A-Z0-9_]/g, "")}_${Date.now()}`;

            const payload = {
                name: this.wizardData.name,
                description: this.wizardData.description,
                version: this.wizardData.version,
                template_key: templateKey,
                json_schema: jsonSchema,
                visible: true,
            };

            const templateId =
                await this.createJustificationTemplateUseCase.execute(payload);

            if (!templateId) {
                throw new Error("Failed to create template");
            }
            ToastManagerStore.success("Plantilla creada exitosamente");

            return templateId;
        } catch (error) {
            console.error("Error creating template:", error);
            ToastManagerStore.error("Error al crear la plantilla");
            return null;
        } finally {
            this.creatingTemplate = false;
        }
    }

    @computed
    get canProceedFromBasics(): boolean {
        return (
            this.wizardData.name.trim().length > 0 &&
            this.wizardData.description.trim().length > 0
        );
    }

    @computed
    get canProceedFromSheets(): boolean {
        // Permitir avanzar sin seleccionar hojas (el usuario puede crear solo hojas personalizadas)
        return true;
    }

    @computed
    get isLoading(): boolean {
        return (
            this.loadingCatalog ||
            this.loadingProviders ||
            this.loadingExpenseOptions ||
            this.loadingModelFields
        );
    }

    private buildJsonSchema(): any {
        // Convert selected sheets from catalog
        const catalogSheets = this.wizardData.selectedSheets.map(
            (selectedSheet) => ({
                name: selectedSheet.customName || selectedSheet.sheetName,
                subtitle: selectedSheet.description,
                fixedFields: selectedSheet.fixedFields || {},
                tables: selectedSheet.tables || [],
                preTableText: selectedSheet.preTableText || [],
                postTableText: selectedSheet.postTableText || [],
                ...(selectedSheet.iterateBy
                    ? { iterateBy: selectedSheet.iterateBy }
                    : {}),
            }),
        );

        // Convert custom sheets to JSON format
        const customSheets = this.wizardData.customSheets.map((customSheet) => {
            if (customSheet.tableType === "generic_expense_rows") {
                // Build filter object from FilterConfig array
                // Note: expense types/subtypes are handled via separate expense_types/expense_subtypes params
                const filters: Record<string, any> = {};

                // Add other filters (not expense types/subtypes)
                (customSheet.filters || []).forEach((filter) => {
                    let fieldName = filter.field;

                    // Append operator suffix to field name
                    if (filter.operator === "isnull") {
                        fieldName = `${filter.field}__isnull`;
                    } else if (filter.operator === "in") {
                        fieldName = `${filter.field}__in`;
                    } else if (filter.operator === "gte") {
                        fieldName = `${filter.field}__gte`;
                    } else if (filter.operator === "lte") {
                        fieldName = `${filter.field}__lte`;
                    } else if (filter.operator === "gt") {
                        fieldName = `${filter.field}__gt`;
                    } else if (filter.operator === "lt") {
                        fieldName = `${filter.field}__lt`;
                    } else if (filter.operator === "icontains") {
                        fieldName = `${filter.field}__icontains`;
                    } else if (filter.operator === "contains") {
                        fieldName = `${filter.field}__contains`;
                    } else if (filter.operator === "iexact") {
                        fieldName = `${filter.field}__iexact`;
                    } else if (filter.operator === "range") {
                        fieldName = `${filter.field}__range`;
                    } else if (filter.operator !== "exact") {
                        fieldName = `${filter.field}__${filter.operator}`;
                    }

                    filters[fieldName] = filter.value;
                });

                // Build order_by array
                const orderBy = (customSheet.orderBy || []).map((order) =>
                    order.direction === "desc"
                        ? `-${order.field}`
                        : order.field,
                );

                // Build column sources mapping
                const columnSources: Record<string, string> = {};
                (customSheet.columns || []).forEach((col) => {
                    if ((col as any).source) {
                        columnSources[col.key] = (col as any).source;
                    }
                });

                return {
                    name: customSheet.name,
                    subtitle: customSheet.description || "",
                    fixedFields: {},
                    tables: [
                        {
                            name: customSheet.name,
                            headers: (customSheet.columns || []).map((col) => ({
                                key: col.key,
                                label: col.label,
                            })),
                            // hasTotalRow not included for generic_expense_rows (not supported)
                            rows: {
                                dataProvider: "get_generic_expense_rows",
                                args: {
                                    project_id: "{{project.id}}",
                                    project_finantial_entity_id:
                                        "{{project_finantial_entity.id}}",
                                    columns: (customSheet.columns || []).map(
                                        (col) => ({
                                            ...((col as any).computed
                                                ? {
                                                      key: col.key,
                                                      computed: (col as any)
                                                          .computed,
                                                  }
                                                : {
                                                      key: col.key,
                                                      source: (col as any)
                                                          .source,
                                                  }),
                                        }),
                                    ),
                                    filters:
                                        Object.keys(filters).length > 0
                                            ? filters
                                            : undefined,
                                    expense_types:
                                        customSheet.selectedExpenseTypes &&
                                        customSheet.selectedExpenseTypes
                                            .length > 0
                                            ? customSheet.selectedExpenseTypes
                                            : undefined,
                                    expense_subtypes:
                                        customSheet.selectedExpenseSubtypes &&
                                        customSheet.selectedExpenseSubtypes
                                            .length > 0
                                            ? customSheet.selectedExpenseSubtypes
                                            : undefined,
                                    order_by:
                                        orderBy.length > 0
                                            ? orderBy
                                            : undefined,
                                    include_indirect_costs:
                                        customSheet.includeIndirectCosts !==
                                        undefined
                                            ? customSheet.includeIndirectCosts
                                            : true,
                                    territories:
                                        customSheet.territories &&
                                        customSheet.territories.length > 0
                                            ? customSheet.territories
                                            : undefined,
                                },
                            },
                        },
                    ],
                    preTableText: [],
                    postTableText: [],
                };
            } else if (customSheet.tableType === "budget-summary") {
                // budget-summary type - uses get_budget_groups_by_mapping
                const budgetConfig = customSheet.budgetSummaryConfig!;

                // Build headers matching provider output columns
                const headers = [
                    { key: "group", label: "Partida presupuestaria" },
                    { key: "budget_total", label: "Coste total del proyecto" },
                    { key: "justified_total", label: "Gastos justificados" },
                    { key: "difference", label: "Diferencia" },
                ];

                // Build metadata object
                const metadata: Record<string, any> = {
                    groupBy: budgetConfig.groupBy,
                    source: "expenses", // Always compute both budget and expenses
                    showCategories: budgetConfig.showCategories,
                };

                if (budgetConfig.showCategories) {
                    metadata.hideEmptyCategories =
                        budgetConfig.hideEmptyCategories;
                    metadata.showCategoryHeaders =
                        budgetConfig.showCategoryHeaders;
                    metadata.showCategoryTotals =
                        budgetConfig.showCategoryTotals;
                }

                if (budgetConfig.includeEmptyRows !== undefined) {
                    metadata.includeEmptyRows = budgetConfig.includeEmptyRows;
                }

                // Add total row configuration to metadata (not as separate property)
                if (customSheet.hasTotalRow) {
                    metadata.total = {
                        label: budgetConfig.totalLabel || "TOTAL",
                    };
                }

                // Build args for data provider (tableConfig NOT included here, extracted from metadata by engine)
                const args: Record<string, any> = {
                    project_id: "{{project.id}}",
                    project_finantial_entity_id:
                        "{{project_finantial_entity.id}}",
                };

                // Add indirect costs filter at args level
                if (customSheet.includeIndirectCosts !== undefined) {
                    args.include_indirect_costs =
                        customSheet.includeIndirectCosts;
                }

                // Add territories filter at args level
                if (
                    customSheet.territories &&
                    customSheet.territories.length > 0
                ) {
                    args.territories = customSheet.territories;
                }

                // Build filters array (OR logic) instead of direct expense_types/expense_subtypes (AND logic)
                const filters: Array<Record<string, any>> = [];

                // Add each expense type as separate filter
                if (
                    customSheet.selectedExpenseTypes &&
                    customSheet.selectedExpenseTypes.length > 0
                ) {
                    for (const expenseType of customSheet.selectedExpenseTypes) {
                        const filter: Record<string, any> = {
                            expense_types: [expenseType],
                        };

                        // Add indirect costs and territories to each individual filter
                        if (customSheet.includeIndirectCosts !== undefined) {
                            filter.include_indirect_costs =
                                customSheet.includeIndirectCosts;
                        }
                        if (
                            customSheet.territories &&
                            customSheet.territories.length > 0
                        ) {
                            filter.territories = customSheet.territories;
                        }

                        filters.push(filter);
                    }
                }

                // Add each expense subtype as separate filter
                if (
                    customSheet.selectedExpenseSubtypes &&
                    customSheet.selectedExpenseSubtypes.length > 0
                ) {
                    for (const expenseSubtype of customSheet.selectedExpenseSubtypes) {
                        const filter: Record<string, any> = {
                            expense_subtypes: [expenseSubtype],
                        };

                        // Add indirect costs and territories to each individual filter
                        if (customSheet.includeIndirectCosts !== undefined) {
                            filter.include_indirect_costs =
                                customSheet.includeIndirectCosts;
                        }
                        if (
                            customSheet.territories &&
                            customSheet.territories.length > 0
                        ) {
                            filter.territories = customSheet.territories;
                        }

                        filters.push(filter);
                    }
                }

                if (filters.length > 0) {
                    args.filters = filters;
                }

                return {
                    name: customSheet.name,
                    subtitle: customSheet.description || "",
                    fixedFields: {},
                    tables: [
                        {
                            name: customSheet.name,
                            headers,
                            hasTotalRow: customSheet.hasTotalRow,
                            metadata,
                            rows: {
                                dataProvider: "get_budget_groups_by_mapping",
                                args,
                            },
                        },
                    ],
                    preTableText: [],
                    postTableText: [],
                };
            } else {
                // Unknown table type fallback
                return {
                    name: customSheet.name,
                    subtitle: customSheet.description || "",
                    fixedFields: {},
                    tables: [],
                    preTableText: [],
                    postTableText: [],
                };
            }
        });

        // Combine both types of sheets
        const allSheets = [...catalogSheets, ...customSheets];

        return {
            templateKey: "", // Will be set during creation
            fileName: `${this.wizardData.name.toLowerCase().replace(/\s+/g, "_")}.xlsx`,
            fileType:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            description: this.wizardData.description,
            version: this.wizardData.version,
            visible: true,
            sheets: allSheets,
            validations: [],
            permissions: {
                editable: true,
                cloneable: true,
                deletable: true,
            },
        };
    }

    @action
    reset(): void {
        this.currentStep = "basics";
        this.wizardData = {
            name: "",
            description: "",
            version: 1,
            selectedSheets: [],
            customSheets: [],
        };
        this.validationResult = null;
    }
}
